import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import FormControl from '@mui/material/FormControl'
import TextField from '@mui/material/TextField'
import Title from '../../component/Title'
import API from '../../component/Axios'
import Snackbars from '../../component/Snackbars'
import BottomButtonGroup from '../../component/BottomButtonGroup'
import Spiner from '../../component/Spiner'
import ListEmptyState from '../../component/ListEmptyState'
import { createListLinkTarget, getLocationState } from '../../utils/listPageState'
import {
    applyShippingFeeDraft,
    clearShippingFeeDraft,
    createEditShippingFeeDraftKey,
    createShippingFeeDraftPayload,
    readShippingFeeDraft,
    saveShippingFeeDraft,
} from './shippingFeeDraftUtils'

const DEFAULT_SEARCH_SELECT = {
    searchType: '',
    searchStatus: '',
}

// 상세 조회 실패 시에도 폼 기본값을 명확하게 되돌릴 수 있게 공통 초기값을 사용한다.
const createDefaultValues = () => ({})

export default function ShippingFee(props) {
    const history = useHistory()
    const location = useLocation()
    const locationState = getLocationState(location)
    const id = props.match.params.adminId
    const listCurPage = locationState.curPage || 1
    const searchText = locationState.searchText || ''
    const searchSelect = locationState.searchSelect || DEFAULT_SEARCH_SELECT
    const listSearch = location.search || ''
    const draftKey = createEditShippingFeeDraftKey(id)
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [draftReady, setDraftReady] = useState(false)
    const [values, setValues] = useState(() => createDefaultValues())
    const [openSnackbar, setOpenSnackbar] = useState(false)
    const [severitySnackbar, setSeveritySnackbar] = useState('info')
    const [messageSnackbar, setMessageSnackbar] = useState('')
    const [detailErrorMessage, setDetailErrorMessage] = useState('')
    const initialDraftSnapshotRef = useRef('')

    const resetDetailState = useCallback((errorMessage) => {
        setValues(createDefaultValues())
        setDraftReady(false)
        setDetailErrorMessage(errorMessage)
    }, [])

    useEffect(() => {
        const getData = async () => {
            if (!id) {
                const errorMessage = '필수 파라미터가 없어 배송비 정보를 불러올 수 없습니다.'
                resetDetailState(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
                setLoading(false)
                return
            }

            setLoading(true)
            setDraftReady(false)
            setDetailErrorMessage('')

            try {
                const { data } = await API.post('/api/v1/admin/shippingFee/getShippingFee', { adminId: id })
                if (data.code === 1) {
                    const baseValues = data.data || {}

                    if (!baseValues.adminId) {
                        resetDetailState('배송비 상세 정보를 찾을 수 없습니다.')
                        return
                    }

                    const storedDraft = readShippingFeeDraft(draftKey)
                    const restoredDraft = applyShippingFeeDraft({
                        baseValues,
                        draft: storedDraft,
                    })

                    initialDraftSnapshotRef.current = JSON.stringify(createShippingFeeDraftPayload({
                        values: baseValues,
                    }))

                    setValues(restoredDraft.values)
                    setDetailErrorMessage('')
                    setDraftReady(true)
                } else if (data.code === 403) {
                    history.push('/login')
                    return
                } else {
                    const errorMessage = data.message || '배송비 정보를 불러오지 못했습니다.'
                    resetDetailState(errorMessage)
                    setOpenSnackbar(true)
                    setSeveritySnackbar('error')
                    setMessageSnackbar(errorMessage)
                }
            } catch (_error) {
                const errorMessage = '배송비 정보를 불러오지 못했습니다.'
                resetDetailState(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
            } finally {
                setLoading(false)
            }
        }
        getData()
    }, [draftKey, history, id, resetDetailState])

    useEffect(() => {
        if (!draftReady || !values?.adminId) {
            return
        }

        // 배송비 상세는 목록 query를 건드리지 않고 편집 draft만 sessionStorage에 보존한다.
        const nextDraft = createShippingFeeDraftPayload({
            values,
        })
        const nextDraftSnapshot = JSON.stringify(nextDraft)

        if (nextDraftSnapshot === initialDraftSnapshotRef.current) {
            clearShippingFeeDraft(draftKey)
            return
        }

        saveShippingFeeDraft(draftKey, nextDraft)
    }, [draftKey, draftReady, values])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    const moveToList = (snackbarState) => {
        // 저장 후에도 운영자가 보던 배송비 목록 조건으로 그대로 복귀시킨다.
        clearShippingFeeDraft(draftKey)
        history.push(createListLinkTarget({
            pathname: '/shippingFees',
            search: listSearch,
            searchText: searchText,
            searchSelect: searchSelect,
            curPage: listCurPage,
            ...(snackbarState ? {
                openSnackbar: true,
                severitySnackbar: snackbarState.severity,
                messageSnackbar: snackbarState.message,
            } : {})
        }))
    }

    const handleUpdateField = (e) => {
        setValues({
            ...values,
            [e.target.name]: e.target.value
        })
    }

    const updateShippingFee= async () => {
        if (saving) {
            return
        }

        const nextShippingPrice = Number(values.shippingPrice)

        if (values.shippingPrice === '' || values.shippingPrice === null || values.shippingPrice === undefined || Number.isNaN(nextShippingPrice)) {
            setOpenSnackbar(true)
            setSeveritySnackbar('warning')
            setMessageSnackbar('배송비를 입력하세요.')
            return
        }

        if (nextShippingPrice < 0) {
            setOpenSnackbar(true)
            setSeveritySnackbar('warning')
            setMessageSnackbar('배송비는 0원 이상이어야 합니다.')
            return
        }

        try {
            setSaving(true)

            const { data } = await API.post('/api/v1/admin/shippingFee/update', { adminId: values.adminId, shippingPrice: nextShippingPrice })
            
            if (data.code === 1) {
                moveToList({ severity: 'success', message: '배송비를 저장했습니다.' })
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message)
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('배송비를 저장하지 못했습니다.')
        } finally {
            setSaving(false)
        }
    }

    const handleSubmit = (e) => {
        e.preventDefault()

        updateShippingFee()
    }

    // 상세 조회 실패 시 목록 화면과 같은 오류 빈 상태 안내를 사용한다.
    const renderDetailErrorState = () => (
        <ListEmptyState
            variant="error"
            title="배송비 정보를 불러오지 못했습니다."
            description={detailErrorMessage || '목록으로 돌아가 다시 확인하세요.'}
            action={(
                <BottomButtonGroup variant="emptyStateAction" onBackClick={() => moveToList()} backLabel="목록으로" />
            )}
            sx={{ mt: 2 }}
        />
    )

    return (
        <Grid item xs={12}>
            {loading ? (
                <>
                    <Spiner/>
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </>
            ) : !values?.adminId ? (
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                    <Title>배송비 수정</Title>
                    {renderDetailErrorState()}
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Paper>
            ) : (
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                    <Title>배송비 수정</Title>
                    <Box component="form" onSubmit={handleSubmit} sx={{ opacity: saving ? 0.6 : 1 }} >
                        <Grid container spacing={2} sx={{ marginTop: 2 }}>
                            <Grid item xs={12} sm={12} md={12}>
                                <FormControl fullWidth>
                                    <TextField
                                        label="배송비"
                                        id="shippingPrice"
                                        name="shippingPrice"
                                        value={values.shippingPrice ?? ''}
                                        onChange={handleUpdateField}
                                        type="number"
                                        size="small"
                                        disabled={saving}
                                    />
                                </FormControl>
                            </Grid>
                        </Grid>
                        <BottomButtonGroup
                            visible={true}
                            submitDisabled={saving}
                            submitLabel={saving ? '저장 중...' : '저장'}
                            backDisabled={saving}
                            onBackClick={() => moveToList()}
                        />
                    </Box>
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Paper>
            )}
        </Grid>
    )
}
